import { supabase } from '../lib/supabase';
import { getAuthHeaders } from '../lib/api';
import { normalizePhone } from '../../lib/utils/phone.js';
import { importPastClients, parseDateValue, sanitizeLifecycleStage, sanitizeEngagement } from './import-past-clients.js';

export { importPastClients };

/**
 * Client Management Service
 * Handles all client-related database operations with proper error handling
 */


/** Fire-and-forget: start Google review sequence if agent has auto-enroll enabled */
async function tryAutoEnrollGoogleReview(clientId) {
  if (!clientId) return;
  try {
    const headers = await getAuthHeaders();
    await fetch('/api/clients/google-review-enroll', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ clientId }),
    });
  } catch (err) {
    console.warn('Google review auto-enroll failed:', err);
  }
}

// Get all clients for the authenticated agent
export const getClients = async () => {
  try {
    const { data, error } = await supabase
      ?.from('clients')
      ?.select(`
        *,
        client_tags (
          id,
          tag_name
        )
      `)
      ?.order('created_at', { ascending: false });

    if (error) {
      return { data: null, error };
    }

    // Transform the data to match the expected format
    const transformedData = data?.map((client) => ({
      ...client,
      tags: client?.client_tags?.map((tag) => tag?.tag_name) || []
    }));

    return { data: transformedData, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};

// Get a single client by ID
export const getClientById = async (clientId) => {
  try {
    const { data, error } = await supabase
      ?.from('clients')
      ?.select(`
        *,
        client_tags (
          id,
          tag_name
        ),
        client_interactions (
          id,
          interaction_type,
          notes,
          scheduled_at,
          completed_at,
          created_at
        )
      `)
      ?.eq('id', clientId)
      ?.single();

    if (error) {
      return { data: null, error };
    }

    // Transform the data
    const transformedData = {
      ...data,
      tags: data?.client_tags?.map((tag) => tag?.tag_name) || [],
      interactions: data?.client_interactions || []
    };

    return { data: transformedData, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};

// Create a new client
export const createClient = async (clientData) => {
  try {
    const { tags, ...clientInfo } = clientData;

    // Get current user
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    // Normalize empty strings to null so UNIQUE constraints allow multiple "no email" clients.
    // Product is past-clients-only — always force lifecycle_stage.
    const payload = {
      ...clientInfo,
      agent_id: user?.id,
      email: clientInfo.email?.trim() || null,
      phone: normalizePhone(clientInfo.phone) || (clientInfo.phone?.trim() || null),
      location: clientInfo.location?.trim() || null,
      notes: clientInfo.notes?.trim() || null,
      lifecycle_stage: 'Past Client',
    };

    // Create client
    const { data: client, error: clientError } = await supabase
      ?.from('clients')
      ?.insert(payload)
      ?.select()
      ?.single();

    if (clientError) {
      return { data: null, error: clientError };
    }

    // Add tags if provided
    if (tags?.length > 0) {
      const tagInserts = tags?.map((tag) => ({
        client_id: client?.id,
        tag_name: tag
      }));

      const { error: tagsError } = await supabase
        ?.from('client_tags')
        ?.insert(tagInserts);

      if (tagsError) {
        console.error('Error adding tags:', tagsError);
      }
    }

    // Auto Google review enrollment is deferred to the hourly cron (google-review-followup-sender)
    // which gates on first_touch_sent_at + 7 days. Do not enroll immediately on contact creation.

    return { data: client, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};

// Update an existing client
export const updateClient = async (clientId, updates) => {
  try {
    const { tags, ...clientUpdates } = updates;

    // Normalize phone/email the same way create/import do
    if (Object.prototype.hasOwnProperty.call(clientUpdates, 'phone')) {
      const raw = clientUpdates.phone;
      clientUpdates.phone = raw
        ? (normalizePhone(raw) || String(raw).trim() || null)
        : null;
    }
    if (Object.prototype.hasOwnProperty.call(clientUpdates, 'email')) {
      const raw = clientUpdates.email;
      clientUpdates.email = raw?.trim() ? String(raw).trim().toLowerCase() : null;
    }
    if (Object.prototype.hasOwnProperty.call(clientUpdates, 'location')) {
      clientUpdates.location = clientUpdates.location?.trim() || null;
    }
    if (Object.prototype.hasOwnProperty.call(clientUpdates, 'notes')) {
      clientUpdates.notes = clientUpdates.notes?.trim() || null;
    }

    // Product is past-clients-only — never allow other lifecycle stages via updates
    clientUpdates.lifecycle_stage = 'Past Client';

    // Update client info
    const { data, error } = await supabase
      ?.from('clients')
      ?.update({
        ...clientUpdates,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', clientId)
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error };
    }

    // Update tags if provided
    if (tags !== undefined) {
      // Delete existing tags
      await supabase?.from('client_tags')?.delete()?.eq('client_id', clientId);

      // Add new tags
      if (tags?.length > 0) {
        const tagInserts = tags?.map((tag) => ({
          client_id: clientId,
          tag_name: tag
        }));

        const { error: tagsError } = await supabase
          ?.from('client_tags')
          ?.insert(tagInserts);

        if (tagsError) {
          console.error('Error updating tags:', tagsError);
        }
      }
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};

// Delete a client
export const deleteClient = async (clientId) => {
  try {
    const { error } = await supabase?.from('clients')?.delete()?.eq('id', clientId);

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (error) {
    return { error: { message: 'Network error. Please try again.' } };
  }
};

// Bulk delete clients
export const bulkDeleteClients = async (clientIds) => {
  try {
    const { error } = await supabase
      ?.from('clients')
      ?.delete()
      ?.in('id', clientIds);

    if (error) {
      return { error };
    }

    return { error: null };
  } catch (error) {
    return { error: { message: 'Network error. Please try again.' } };
  }
};

// Add interaction to a client
export const addClientInteraction = async (clientId, interactionData) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    const { data, error } = await supabase
      ?.from('client_interactions')
      ?.insert({
        client_id: clientId,
        agent_id: user?.id,
        ...interactionData
      })
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};

// Get client interactions
export const getClientInteractions = async (clientId) => {
  try {
    const { data, error } = await supabase
      ?.from('client_interactions')
      ?.select('*')
      ?.eq('client_id', clientId)
      ?.order('created_at', { ascending: false });

    if (error) {
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};

// Bulk operations for multiple clients
export const bulkUpdateClients = async (clientIds, updates) => {
  try {
    // Product is past-clients-only — never allow other lifecycle stages
    const safeUpdates = { ...updates, lifecycle_stage: 'Past Client' };
    const { data, error } = await supabase
      ?.from('clients')
      ?.update({
        ...safeUpdates,
        updated_at: new Date()?.toISOString()
      })
      ?.in('id', clientIds)
      ?.select();

    if (error) {
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};

// Add tags to multiple clients
export const bulkAddTags = async (clientIds, tags) => {
  try {
    const tagInserts = [];
    clientIds?.forEach((clientId) => {
      tags?.forEach((tag) => {
        tagInserts?.push({
          client_id: clientId,
          tag_name: tag
        });
      });
    });

    const { data, error } = await supabase
      ?.from('client_tags')
      ?.insert(tagInserts)
      ?.select();

    if (error) {
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};

// Export clients data as CSV format
export const exportClientsData = async (clientIds = []) => {
  try {
    let query = supabase?.from('clients')?.select(`
      name,
      email,
      phone,
      location,
      lifecycle_stage,
      engagement_level,
      last_interaction,
      last_interaction_type,
      notes,
      created_at,
      client_tags (tag_name)
    `);

    if (clientIds?.length > 0) {
      query = query?.in('id', clientIds);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });

    if (error) {
      return { data: null, error };
    }

    // Transform data for CSV export
    const csvData = data?.map((client) => ({
      Name: client?.name,
      Email: client?.email || '',
      Phone: client?.phone || '',
      Address: client?.location || '',
      'Lifecycle Stage': client?.lifecycle_stage,
      'Engagement Level': client?.engagement_level,
      'Last Interaction': client?.last_interaction ? new Date(client?.last_interaction)?.toLocaleDateString() : '',
      'Last Interaction Type': client?.last_interaction_type || '',
      Tags: client?.client_tags?.map((tag) => tag?.tag_name)?.join(', ') || '',
      Notes: client?.notes || '',
      'Created Date': new Date(client?.created_at)?.toLocaleDateString()
    }));

    return { data: csvData, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};

// Real-time subscription for clients
export const subscribeToClients = (callback) => {
  const subscription = supabase
    ?.channel('clients_channel')
    ?.on('postgres_changes', 
      { event: '*', schema: 'public', table: 'clients' }, 
      callback
    )
    ?.subscribe();

  return () => {
    supabase?.removeChannel(subscription);
  };
};

// Get client statistics
export const getClientStats = async () => {
  try {
    const { data, error } = await supabase
      ?.from('clients')
      ?.select('lifecycle_stage, engagement_level, created_at');

    if (error) {
      return { data: null, error };
    }

    const stats = {
      totalClients: data?.length || 0,
      activeLeads: data?.filter((c) => c?.lifecycle_stage === 'Lead')?.length || 0,
      underContract: data?.filter((c) => c?.lifecycle_stage === 'Under Contract')?.length || 0,
      closedThisMonth: data?.filter((c) => {
        if (c?.lifecycle_stage !== 'Closed') return false;
        const clientDate = new Date(c?.created_at);
        const now = new Date();
        return clientDate?.getMonth() === now?.getMonth() && 
               clientDate?.getFullYear() === now?.getFullYear();
      })?.length || 0,
      highEngagement: data?.filter((c) => c?.engagement_level === 'High')?.length || 0,
      mediumEngagement: data?.filter((c) => c?.engagement_level === 'Medium')?.length || 0,
      lowEngagement: data?.filter((c) => c?.engagement_level === 'Low')?.length || 0
    };

    return { data: stats, error: null };
  } catch (error) {
    return { data: null, error: { message: 'Network error. Please try again.' } };
  }
};
// Import clients from CSV data (classic wizard) — maps rows then uses chunked upsert
export const importClientsFromCSV = async (importData) => {
  try {
    const { csvHeaders, csvData, mappings, options } = importData;

    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) {
      return { data: null, error: { message: 'User not authenticated' } };
    }

    const validColumns = ['name', 'email', 'phone', 'avatar_url', 'location',
                          'lifecycle_stage', 'engagement_level', 'notes', 'birthday', 'home_closing_date'];

    const clientsToImport = [];
    const errors = [];

    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      const clientData = {};
      const addressParts = [];

      csvHeaders.forEach((header, index) => {
        const systemField = mappings[header];
        if (systemField && row[index]) {
          const value = String(row[index]).trim();

          if (systemField === 'tags') {
            clientData[systemField] = value.split(',').map(tag => tag.trim()).filter(tag => tag);
          } else if (systemField === 'name' && !clientData.name) {
            clientData.name = value;
          } else if (systemField === 'firstName') {
            clientData.name = value + (clientData.name ? ' ' + clientData.name : '');
          } else if (systemField === 'lastName') {
            clientData.name = (clientData.name || '') + ' ' + value;
          } else if (systemField === 'address' || systemField === 'city' ||
                     systemField === 'state' || systemField === 'zipCode') {
            addressParts.push(value);
          } else if (systemField === 'lifecycleStage') {
            clientData.lifecycle_stage = sanitizeLifecycleStage(value, 'Past Client');
          } else if (systemField === 'engagementLevel') {
            clientData.engagement_level = sanitizeEngagement(value, 'Medium');
          } else if (systemField === 'birthday') {
            clientData.birthday = parseDateValue(value);
          } else if (systemField === 'homeClosingDate') {
            clientData.home_closing_date = parseDateValue(value);
          } else if (systemField === 'phone') {
            clientData.phone = normalizePhone(value) || value;
          } else if (systemField === 'email') {
            clientData.email = value.toLowerCase();
          } else if (validColumns.includes(systemField)) {
            clientData[systemField] = value;
          }
        }
      });

      if (addressParts.length > 0 && !clientData.location) {
        clientData.location = addressParts.join(', ');
      }

      if (!clientData.name) {
        errors.push(`Row ${i + 1}: Missing required field 'name'`);
        continue;
      }

      if (!clientData.email && !clientData.phone && options.updateExisting) {
        errors.push(`Row ${i + 1}: Missing phone - cannot check for existing clients`);
      }

      clientsToImport.push({
        ...clientData,
        agent_id: user.id
      });
    }

    if (errors.length > 0) {
      console.warn('Import validation errors:', errors);
    }

    if (clientsToImport.length === 0) {
      return {
        data: null,
        error: { message: 'No valid clients to import. Please check your CSV data and field mappings.' }
      };
    }

    const result = await importPastClients(
      clientsToImport.map((c) => ({
        name: c.name,
        email: c.email,
        phone: c.phone,
        location: c.location,
        notes: c.notes,
        lifecycleStage: 'Past Client',
        engagementLevel: c.engagement_level || 'Medium',
        birthday: c.birthday,
        homeClosingDate: c.home_closing_date,
        tags: c.tags || [],
      })),
      {
        defaultLifecycleStage: 'Past Client',
        updateExisting: Boolean(options?.updateExisting),
      }
    );

    if (result.data && errors.length) {
      result.data.errors = [...errors, ...(result.data.errors || [])];
    }
    return result;
  } catch (error) {
    return { data: null, error: { message: error.message || 'Import failed. Please try again.' } };
  }
};
